import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import pick from 'lodash/pick';
import uniqueId from 'lodash/uniqueId';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';

import SnapToCursor from 'components/SnapToCursor/SnapToCursor';
import useOverElement from 'hooks/useOverElement';
import { groupByModulo } from 'utils';
import { ROUTES } from 'utils/routes';
import {
  CMSClient,
  CMSClientsSection,
} from 'utils/sharedStrapiQueries/sharedTypes';
import { breakpointDesktopLarge } from 'utils/styles/vars';

import * as S from '../AboutPage.styles';

const COLUMNS_AMOUNT = 4;

const groupByNames = (collection: Array<CMSClient>, minimumInColumn) => {
  const alphabetically = groupBy(collection, c => c.name[0].toUpperCase());
  const entries: [string, CMSClient[]][] = [];

  Object.entries(alphabetically).forEach(([key, value]) => {
    if (
      entries[entries.length - 1] &&
      entries[entries.length - 1][1].length < minimumInColumn
    ) {
      entries[entries.length - 1][0] += key;
      entries[entries.length - 1][1].push(...value);
    } else {
      entries.push([key, value]);
    }
  });

  return entries;
};

const ClientsSection: React.FunctionComponent<CMSClientsSection> = ({
  clients,
  industries: availableIndustries,
  mainCategory,
  mainCategoryTextColor,
  mainCategoryDefaultTextColors,
  sectionTitle,
}) => {
  const router = useRouter();

  const { width } = useWindowSize();
  const clientsAlphabetically = useMemo(
    () =>
      groupByNames(clients, Math.floor(clients.length / COLUMNS_AMOUNT ** 2)),
    [clients]
  );
  const clientsColumns = groupByModulo(clientsAlphabetically, COLUMNS_AMOUNT);

  const industryFilters = map(availableIndustries, i =>
    pick(i, 'industry', 'textColor', 'defaultTextColors')
  );

  const [filter, setFilter] = useState<string | false>(false);

  const [ref] = useOverElement();

  const filterResetItem = (
    <li key={uniqueId()}>
      <S.ClientsSectionFilter
        active={!filter}
        onClick={() => setFilter(false)}
        backgroundColor={mainCategoryTextColor ?? mainCategoryDefaultTextColors}
      >
        <S.ClientsSectionFilterInner
          size="body"
          text={mainCategory}
          color={mainCategoryTextColor ?? mainCategoryDefaultTextColors}
        />
      </S.ClientsSectionFilter>
    </li>
  );

  const filters = (
    <S.ClientsSectionFilters>
      {filterResetItem}

      {industryFilters.map(({ industry, textColor, defaultTextColors }) => {
        const filteredClients = clients.filter(client =>
          client.industries.find(
            industryWrapper => industryWrapper.industry === industry
          )
        );
        const filterContainsCaseStudy = !!filteredClients.find(
          client => !!client.case_study_page?.slug
        );

        return (
          <li key={uniqueId()}>
            <S.ClientsSectionFilter
              active={industry === filter}
              onClick={() =>
                filterContainsCaseStudy
                  ? setFilter(industry)
                  : router.push(ROUTES.WORK)
              }
              backgroundColor={textColor ?? defaultTextColors}
            >
              <S.ClientsSectionFilterInner
                size="body"
                text={industry}
                color={textColor ?? defaultTextColors}
              />
            </S.ClientsSectionFilter>
          </li>
        );
      })}
    </S.ClientsSectionFilters>
  );

  const client = useCallback(
    ({
      name,
      industries,
      image,
      case_study_page,
      textColor,
      defaultTextColors,
    }) => {
      const caseStudySlug = case_study_page?.slug;
      const item = (
        <S.ClientsSublistItem
          size="small"
          text={name}
          active={!filter || industries.find(e => e.industry === filter)}
          color={textColor ?? defaultTextColors}
        />
      );
      const itemWithLink = caseStudySlug ? (
        <S.Link>
          <Link href={`${ROUTES.WORK}/${caseStudySlug}`}>{item}</Link>
        </S.Link>
      ) : (
        item
      );

      return (
        <S.ClientLi key={uniqueId()}>
          <SnapToCursor
            overlay
            snap={
              image && (
                <S.ClientsBackgroundImage
                  src={image.url}
                  alt={image.alternativeText}
                />
              )
            }
          >
            {itemWithLink}
          </SnapToCursor>
        </S.ClientLi>
      );
    },
    [filter]
  );

  const clientsList = useMemo(
    () => (
      <S.ClientsSectionList>
        {clientsColumns.map(clients => (
          <S.ClientSectionColumn key={uniqueId()}>
            {clients.map(([key, value]) => (
              <S.ClientsListLi key={uniqueId()}>
                <S.ClientsSublistHeader>{key}</S.ClientsSublistHeader>

                <S.ClientsSectionSublist>
                  {value.map(client)}
                </S.ClientsSectionSublist>
              </S.ClientsListLi>
            ))}
          </S.ClientSectionColumn>
        ))}
      </S.ClientsSectionList>
    ),
    [clientsAlphabetically, client]
  );

  const mobileClientsList = (
    <S.MobileClientsListWrapper scrollbar="top">
      <S.ClientsSectionList length={clientsAlphabetically.length}>
        {clientsAlphabetically.map(([key, value]) => (
          <S.ClientsListLi key={uniqueId()}>
            <S.ClientsSublistHeader as="h5">{key}</S.ClientsSublistHeader>

            <S.ClientsSectionSublist>
              {value.map(client)}
            </S.ClientsSectionSublist>
          </S.ClientsListLi>
        ))}
      </S.ClientsSectionList>
    </S.MobileClientsListWrapper>
  );

  return (
    <S.ClientsSectionWrapper ref={ref}>
      <S.ClientsSectionHeadline text={sectionTitle} size="heading2" />

      {filters}

      {width >= breakpointDesktopLarge ? clientsList : mobileClientsList}
    </S.ClientsSectionWrapper>
  );
};

export default ClientsSection;
